import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useGetSwiftlaneUserGroups } from '@/api/keyless/swiftlane/get-swiftlane-user-groups'
import { PasswordInputFormField } from '@/components/form'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { TabActions } from '@/components/layout/tab-card'
import { toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { ProviderCard } from './provider-card'
import { useSaveSwiftlaneCredentials } from './provider-connections'
import { ProviderField } from './provider-field'
import { maskSecret, ProviderDirtyChange, useProviderEdit } from './use-provider-edit'

interface SwiftlaneFormData {
  authToken: string
  config: string
  swiftlaneAccountName: string
}

interface SwiftlaneCardProps {
  buildingId: number
  onDirtyChange: ProviderDirtyChange
  tabs: ReactNode
}

const SERVER_VALUES: SwiftlaneFormData = { authToken: '', config: '', swiftlaneAccountName: '' }

const isJson = (value: string) => {
  try {
    JSON.parse(value)
    return true
  } catch {
    return false
  }
}

export const SwiftlaneCard = ({ buildingId, onDirtyChange, tabs }: SwiftlaneCardProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  // Note: there is no status endpoint, a successful user-group lookup is the only sign of a connection
  const { isError, isLoading, isSuccess } = useGetSwiftlaneUserGroups({ buildingId })
  const { mutateAsync: save, isPending } = useSaveSwiftlaneCredentials()

  const schema = useMemo(
    () =>
      z.object({
        authToken: z
          .string()
          .trim()
          .min(1, t('organisation:places.integrations.swiftlane.errors.authToken')),
        config: z
          .string()
          .trim()
          .min(1, t('organisation:places.integrations.swiftlane.errors.config'))
          .refine(isJson, t('organisation:places.accessControl.validation.configJson')),
        swiftlaneAccountName: z
          .string()
          .trim()
          .min(1, t('organisation:places.integrations.swiftlane.errors.accountName')),
      }),
    [t],
  )

  const form = useForm<SwiftlaneFormData>({
    defaultValues: SERVER_VALUES,
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const changeFields = useMemo<FormChangeFields<SwiftlaneFormData>>(
    () => ({
      authToken: {
        format: maskSecret,
        label: t('organisation:places.integrations.swiftlane.authTokenField.label'),
      },
      config: { label: t('organisation:places.integrations.swiftlane.configField.label') },
      swiftlaneAccountName: {
        label: t('organisation:places.integrations.swiftlane.accountNameField.label'),
      },
    }),
    [t],
  )

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  const { cancel, isEditing, start, stop } = useProviderEdit({
    form,
    getChanges,
    onDirtyChange,
    serverValues: SERVER_VALUES,
  })

  const handleSave = form.handleSubmit(async (values) => {
    try {
      await save({ buildingId, ...values })
      toastFactory.success(t('organisation:places.integrations.keyless.toasts.saved'))
      stop()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  })

  const connected = isSuccess && !isError

  return (
    <FormProvider {...form}>
      <ProviderCard
        actions={
          connected ? undefined : (
            <TabActions
              isEditing={isEditing}
              isSaving={isPending}
              onCancel={cancel}
              onEdit={start}
              onSave={handleSave}
            />
          )
        }
        configured={connected}
        loading={isLoading}
        notes={connected ? [t('organisation:places.accessControl.credentials.lockedNote')] : []}
        skeletonGroups={[
          {
            labels: [
              t('organisation:places.integrations.swiftlane.authTokenField.label'),
              t('organisation:places.integrations.swiftlane.accountNameField.label'),
              t('organisation:places.integrations.swiftlane.configField.label'),
            ],
          },
        ]}
        tabs={tabs}
      >
        {!connected && (
          <>
            <ProviderField
              required
              isEditing={isEditing}
              label={t('organisation:places.integrations.swiftlane.authTokenField.label')}
            >
              <PasswordInputFormField
                autoComplete="new-password"
                name="authToken"
                placeholder={t(
                  'organisation:places.integrations.swiftlane.authTokenField.placeholder',
                )}
                size="small"
              />
            </ProviderField>
            <ProviderField
              required
              isEditing={isEditing}
              label={t('organisation:places.integrations.swiftlane.accountNameField.label')}
            >
              <TextInputFormField
                clearable
                withRestore
                autoComplete="off"
                name="swiftlaneAccountName"
                placeholder={t(
                  'organisation:places.integrations.swiftlane.accountNameField.placeholder',
                )}
                size="small"
              />
            </ProviderField>
            <ProviderField
              required
              isEditing={isEditing}
              label={t('organisation:places.integrations.swiftlane.configField.label')}
            >
              <TextAreaFormField
                maxLength={5000}
                name="config"
                placeholder={t(
                  'organisation:places.integrations.swiftlane.configField.placeholder',
                )}
              />
            </ProviderField>
          </>
        )}
      </ProviderCard>
    </FormProvider>
  )
}
